<script setup lang="ts">
import * as Sentry from "@sentry/vue";
import { useQueryClient } from "@tanstack/vue-query";
import { twMerge } from "tailwind-merge";
import { computed, ref, useTemplateRef, watchEffect } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { toast } from "vue-sonner";

import { postReceipt } from "@/api";
import { useUser } from "@/composables/api/useUser";
import { compressImage } from "@/utils/compressImage";

import PhotoIcon from "@/components/icons/PhotoIcon.vue";
import ReceiptIcon from "@/components/icons/ReceiptIcon.vue";
import UploadIcon from "@/components/icons/UploadIcon.vue";
import FileUpload from "@/components/sections/submit-receipt/FileUpload.vue";
import Button from "@/components/ui/Button.vue";
import Dialog from "@/components/ui/Dialog.vue";
import Spinner from "@/components/ui/Spinner.vue";
import QrcodeVue from "qrcode.vue";

defineProps<{
  buttonClass?: string;
}>();

const { t } = useI18n();
const router = useRouter();
const queryClient = useQueryClient();
const { data: user } = useUser();
const fileUploadRef = useTemplateRef("fileUpload");

const submissionMode = ref<"upload" | "qrcode" | null>(null);
const selectedFiles = ref<File[]>([]);
const isLoading = ref(false);
const isDialogOpen = ref(false);
const qrCodeValue = computed(
  () => `${window.location.origin}/tableau-de-bord/photographier-mon-ticket?uid=${user.value?.id}`,
);

async function handleSubmitReceipt() {
  if (selectedFiles.value.length === 0 || isLoading.value) {
    return;
  }

  isLoading.value = true;

  try {
    // Compress files before uploading
    const compressedFiles = await Promise.all(
      selectedFiles.value.map(async (file) => {
        // Only compress image files
        if (!file.type.startsWith("image/")) {
          return file;
        }

        return await compressImage(file);
      }),
    );

    const { response } = await postReceipt(compressedFiles);

    if (!response.ok) {
      if (response.status === 413) {
        throw new Error("Payload Too Large");
      }
      throw new Error(`Failed to submit receipt: ${response.status} ${response.statusText}`);
    } else {
      isDialogOpen.value = false;
      queryClient.invalidateQueries({ queryKey: ["receipts"] });
      router.push({ name: "receipts" });
      toast(t("toast.submitReceiptSuccess"), { position: "top-center" });
    }
  } catch (error) {
    console.error("Error submitting receipt:", error);
    selectedFiles.value = [];

    if (fileUploadRef.value?.inputRef?.value) {
      fileUploadRef.value.inputRef.value = "";
    }

    if (error instanceof Error && error.message.includes("Payload Too Large")) {
      // Track 413 errors after compression in Sentry
      Sentry.captureException(error, {
        tags: {
          component: "SubmitReceipt",
          errorType: "file_too_large_after_compression",
        },
        extra: {
          originalFiles: selectedFiles.value.map((f: File) => ({
            name: f.name,
            size: f.size,
            type: f.type,
          })),
        },
      });

      toast(t("toast.receiptTooLarge"), {
        position: "top-center",
      });
    } else {
      toast(t("toast.submitReceiptError"), { position: "top-center" });
    }
  }

  isLoading.value = false;
}

function goToPicturePage() {
  router.push({ name: "take-picture-of-ticket" });
}

watchEffect(() => {
  if (!isDialogOpen.value) {
    submissionMode.value = null;
    selectedFiles.value = [];
  }
});
</script>

<template>
  <Dialog v-model:open="isDialogOpen">
    <template #trigger>
      <Button :class="buttonClass">
        {{ $t("home.submitReceipt") }}
        <ReceiptIcon />
      </Button>
    </template>

    <template #content>
      <div v-if="!submissionMode" class="flex w-full flex-col gap-8">
        <h1
          :class="
            twMerge(
              'text-galbani text-2xl font-semibold',
              user?.current_mechanic === 'lactel' && 'text-lactel-blue',
            )
          "
        >
          {{ $t("dialog.submitReceipt.title") }}
        </h1>

        <div class="flex flex-col gap-4">
          <Button class="lg:hidden" variant="secondary" @click="goToPicturePage">
            <PhotoIcon />

            <span
              class="w-full [&>span]:font-semibold"
              v-html="$t('dialog.submitReceipt.takePhoto')"
            />
          </Button>

          <Button class="hidden lg:flex" variant="secondary" @click="submissionMode = 'qrcode'">
            <PhotoIcon />

            <span
              class="w-full [&>span]:font-semibold"
              v-html="$t('dialog.submitReceipt.takePhotoWithPhone')"
            />
          </Button>

          <Button variant="topchef" @click="submissionMode = 'upload'">
            <UploadIcon />

            <span
              class="w-full [&>span]:font-semibold"
              v-html="$t('dialog.submitReceipt.uploadFile')"
            />
          </Button>
        </div>
      </div>

      <div v-if="submissionMode === 'upload'" class="flex w-full flex-col gap-8">
        <h1
          :class="
            twMerge(
              'text-galbani text-2xl font-semibold',
              user?.current_mechanic === 'lactel' && 'text-lactel-blue',
            )
          "
        >
          {{ $t("dialog.uploadFile.title") }}
        </h1>

        <FileUpload ref="fileUpload" v-model="selectedFiles" />

        <Button
          variant="secondary"
          :disabled="selectedFiles.length === 0 || isLoading"
          @click="handleSubmitReceipt"
        >
          <template v-if="!isLoading">{{ $t("dialog.uploadFile.submitButton") }}</template>

          <Spinner
            v-else
            class="size-7"
            trackColor="rgba(255, 255, 255, 0.3)"
            color="rgba(255, 255, 255, 1)"
          />
        </Button>
      </div>

      <div v-else-if="submissionMode === 'qrcode'" class="flex w-full flex-col gap-8">
        <h1
          :class="
            twMerge(
              'text-galbani text-2xl font-semibold',
              user?.current_mechanic === 'lactel' && 'text-lactel-blue',
            )
          "
        >
          {{ $t("dialog.qrcode.title") }}
        </h1>

        <p class="text-secondary text-xl font-medium">
          {{ $t("dialog.qrcode.description") }}
        </p>

        <div class="flex min-h-64 w-full items-center justify-center rounded-lg bg-[#D4DBEC] py-10">
          <QrcodeVue :value="qrCodeValue" :size="128" background="#FFF" foreground="#000" />
        </div>
      </div>
    </template>
  </Dialog>
</template>
